import { computed, ref } from 'vue'

import basketStore from '@shared/store/basket'
import partnerStore from '@shared/store/partner'

import useI18n from '@shared/hooks/useI18n'

import { logInfo } from '@shared/helpers/Logger'
import { getDistributionPartnerNumber } from '@shared/helpers/Partner'
import { BasketUtil } from '@shared/utils/Basket'

// HOOKS
const { selectedLanguage } = useI18n()

// GLOBAL DATA
const isChatBotInitialised = ref(false)

const CHATBOT_EVENT = {
  LOADED: 'sanitas_chat-enduser_loaded',
  INIT: 'sanitas_chat-enduser_init',
  UPDATE: 'sanitas_chat-enduser_update-init-data',
}

export default function useChatBot() {

  // COMPUTED
  const chatBotPayload = computed(() => {
    const context = document.body.dataset.trackApp !== import.meta.env.VITE_APP_ID ? 'WEBSITE' : window.location.hostname.includes('calculator') ? 'DVP' : 'DIGITALADVISOR'
    const contractOwner = basketStore.basket.persons.find(p => p.personId === basketStore.basket.contractOwner?.personId)
    const partnerNumber = contractOwner?.contractNumber ? BasketUtil.getCustomerNumberFromContract(contractOwner.contractNumber): null
    return {
      detail: {
        collectiveNumber: basketStore.basket.collective?.contractNumber,
        context,
        contractStartDate: basketStore.basket.contractStartDate,
        distributionPartnerNumber: partnerStore.basketDistributionPartnerNumber.value,
        existingCustomer: !!basketStore.basket.existingCustomer,
        language: basketStore.basket.language,
        partnerNumber,
      }
    }
  })

  const isFabActive = computed(() => !import.meta.env.VITE_FEATURE_FLAG_CHATBOT || (!!import.meta.env.VITE_FEATURE_FLAG_CHATBOT && selectedLanguage.value !== 'DE'))

  // METHODS

  function initChatBot() {
    if (isChatBotInitialised.value || Object.keys(basketStore.basket).length === 0) return

    logInfo(['%cChatBot', 'color: purple', 'Loading chatBot via script'])
    chatBotHandshake(chatBotPayload.value)
    loadChatBotScript()
    isChatBotInitialised.value = true
  }

  function chatBotHandshake(payload) {
    window.addEventListener(CHATBOT_EVENT.LOADED, () => {
      logInfo(['%cChatBot', 'color: purple', CHATBOT_EVENT.INIT, payload])
      window.dispatchEvent(new CustomEvent(CHATBOT_EVENT.INIT, payload))
    })
  }

  function loadChatBotScript() {
    const chatBotScript = document.createElement('script')
    chatBotScript.setAttribute('type', 'module')
    chatBotScript.setAttribute('src', import.meta.env.VITE_CHAT_UI_LOADER)
    document.body.appendChild(chatBotScript)
  }

  function updateChatBot(newBasket, oldBasket) {
    const isWww = document.body.dataset.trackApp !== import.meta.env.VITE_APP_ID
    if ((!isWww && !isChatBotInitialised.value) || Object.keys(oldBasket).length === 0) return

    const hasPayloadDataChanged =
      newBasket.existingCustomer !== oldBasket.existingCustomer
      || newBasket.language !== oldBasket.language
      || getDistributionPartnerNumber(newBasket.distributionPartners) !== getDistributionPartnerNumber(oldBasket.distributionPartners)
      || newBasket.contractStartDate !== oldBasket.contractStartDate

    if (!hasPayloadDataChanged) return

    logInfo(['%cChatBot', 'color: purple', CHATBOT_EVENT.UPDATE, chatBotPayload.value])
    window.dispatchEvent(new CustomEvent(CHATBOT_EVENT.UPDATE, chatBotPayload.value))

  }

  return {
    isFabActive,
    initChatBot,
    updateChatBot,
  }
}
